import * as React from "react";

import {
  ActionService,
  ImageRegistryService,
  TriggerService,
} from "@/shared/domain/actions";
import { AdminService } from "@/shared/domain/admin";
import { AgentService } from "@/shared/domain/agents";
import { AgentThreadService } from "@/shared/domain/agentThread";
import { CollectionService } from "@/shared/domain/collections";
import { CommentService } from "@/shared/domain/comments";
import { ConfigService } from "@/shared/domain/config";
import { CustomFieldService } from "@/shared/domain/customFields";
import { DashboardService } from "@/shared/domain/dashboards";
import { DatasetService } from "@/shared/domain/datasets";
import { DeviceService } from "@/shared/domain/devices";
import { EventService } from "@/shared/domain/events";
import { ExperienceService } from "@/shared/domain/experiences";
import { FeatureFlagService } from "@/shared/domain/featureFlags";
import { FileService } from "@/shared/domain/files";
import { HealthService } from "@/shared/domain/health";
import { IntegrationService } from "@/shared/domain/integrations";
import { InviteService } from "@/shared/domain/invites";
import { LayoutService } from "@/shared/domain/layouts";
import { LimitService } from "@/shared/domain/limits/LimitService";
import { McpRegistryService } from "@/shared/domain/mcp";
import { MeteringService } from "@/shared/domain/metering/MeteringService";
import { MetricsService } from "@/shared/domain/metrics";
import { NotificationService } from "@/shared/domain/notifications";
import { OrgService } from "@/shared/domain/orgs";
import { QueryService } from "@/shared/domain/query";
import { SecretService } from "@/shared/domain/secrets/SecretService";
import { SessionService } from "@/shared/domain/sessions";
import { SkillService } from "@/shared/domain/skills";
import { SlackService } from "@/shared/domain/slack";
import { TokenService } from "@/shared/domain/tokens";
import { TopicService } from "@/shared/domain/topics";
import { UrdfService } from "@/shared/domain/urdf";
import { UserGroupService } from "@/shared/domain/userGroups";
import { UserService } from "@/shared/domain/users/UserService";
import { WorkspaceService } from "@/shared/domain/workspaces/WorkspaceService";

// Keys are ordered alphabetically. When adding a service, update this
// interface, `defaultDomainContextFactory` below, and the instantiation
// map in `DomainServicesProvider` together; the three lists must stay
// aligned for the provider to satisfy the `Context` type.
interface Context {
  actionService: ActionService;
  adminService: AdminService;
  agentService: AgentService;
  agentThreadService: AgentThreadService;
  collections: CollectionService;
  commentService: CommentService;
  configService: ConfigService;
  customFieldService: CustomFieldService;
  dashboardService: DashboardService;
  datasetService: DatasetService;
  devices: DeviceService;
  events: EventService;
  experienceService: ExperienceService;
  featureFlagService: FeatureFlagService;
  files: FileService;
  healthService: HealthService;
  imageRegistryService: ImageRegistryService;
  integrationService: IntegrationService;
  inviteService: InviteService;
  layoutService: LayoutService;
  limitSummary: LimitService;
  mcpRegistryService: McpRegistryService;
  meteringService: MeteringService;
  metricsService: MetricsService;
  notifications: NotificationService;
  orgService: OrgService;
  queryService: QueryService;
  secretService: SecretService;
  sessionService: SessionService;
  skillService: SkillService;
  slackService: SlackService;
  tokens: TokenService;
  topics: TopicService;
  triggerService: TriggerService;
  urdf: UrdfService;
  userGroupService: UserGroupService;
  users: UserService;
  workspaceService: WorkspaceService;
}

export const defaultDomainContextFactory = (overrides?: Partial<Context>) => ({
  get actionService(): ActionService {
    throw new Error("ActionService is missing in DomainServicesContext");
  },
  get adminService(): AdminService {
    throw new Error("AdminService is missing in DomainServicesContext");
  },
  get agentService(): AgentService {
    throw new Error("AgentService is missing in DomainServicesContext");
  },
  get agentThreadService(): AgentThreadService {
    throw new Error("AgentThreadService is missing in DomainServicesContext");
  },
  get collections(): CollectionService {
    throw new Error("CollectionService is missing in DomainServicesContext");
  },
  get commentService(): CommentService {
    throw new Error("CommentService is missing in DomainServicesContext");
  },
  get configService(): ConfigService {
    throw new Error("ConfigService is missing in DomainServicesContext");
  },
  get customFieldService(): CustomFieldService {
    throw new Error("CustomFieldService is missing in DomainServicesContext");
  },
  get dashboardService(): DashboardService {
    throw new Error("DashboardService is missing in DomainServicesContext");
  },
  get datasetService(): DatasetService {
    throw new Error("DatasetService is missing in DomainServicesContext");
  },
  get devices(): DeviceService {
    throw new Error("DeviceService is missing in DomainServicesContext");
  },
  get events(): EventService {
    throw new Error("EventService is missing in DomainServicesContext");
  },
  get experienceService(): ExperienceService {
    throw new Error("ExperienceService is missing in DomainServicesContext");
  },
  get featureFlagService(): FeatureFlagService {
    throw new Error("FeatureFlagService is missing in DomainServicesContext");
  },
  get files(): FileService {
    throw new Error("FileService is missing in DomainServicesContext");
  },
  get healthService(): HealthService {
    throw new Error("HealthService is missing in DomainServicesContext");
  },
  get imageRegistryService(): ImageRegistryService {
    throw new Error("ImageRegistryService is missing in DomainServicesContext");
  },
  get integrationService(): IntegrationService {
    throw new Error("IntegrationService is missing in DomainServicesContext");
  },
  get inviteService(): InviteService {
    throw new Error("InviteService is missing in DomainServicesContext");
  },
  get layoutService(): LayoutService {
    throw new Error("LayoutService is missing in DomainServicesContext");
  },
  get limitSummary(): LimitService {
    throw new Error("LimitService is missing in DomainServicesContext");
  },
  get mcpRegistryService(): McpRegistryService {
    throw new Error("McpRegistryService is missing in DomainServicesContext");
  },
  get meteringService(): MeteringService {
    throw new Error("MeteringService is missing in DomainServicesContext");
  },
  get metricsService(): MetricsService {
    throw new Error("MetricsService is missing in DomainServicesContext");
  },
  get notifications(): NotificationService {
    throw new Error("NotificationService is missing in DomainServicesContext");
  },
  get orgService(): OrgService {
    throw new Error("OrgService is missing in DomainServicesContext");
  },
  get queryService(): QueryService {
    throw new Error("QueryService is missing in DomainServicesContext");
  },
  get secretService(): SecretService {
    throw new Error("SecretService is missing in DomainServicesContext");
  },
  get sessionService(): SessionService {
    throw new Error("SessionService is missing in DomainServicesContext");
  },
  get skillService(): SkillService {
    throw new Error("SkillService is missing in DomainServicesContext");
  },
  get slackService(): SlackService {
    throw new Error("SlackService is missing in DomainServicesContext");
  },
  get tokens(): TokenService {
    throw new Error("TokenService is missing in DomainServicesContext");
  },
  get topics(): TopicService {
    throw new Error("TopicService is missing in DomainServicesContext");
  },
  get triggerService(): TriggerService {
    throw new Error("TriggerService is missing in DomainServicesContext");
  },
  get urdf(): UrdfService {
    throw new Error("UrdfService is missing in DomainServicesContext");
  },
  get userGroupService(): UserGroupService {
    throw new Error("UserGroupService is missing in DomainServicesContext");
  },
  get users(): UserService {
    throw new Error("UserService is missing in DomainServicesContext");
  },
  get workspaceService(): WorkspaceService {
    throw new Error("WorkspaceService is missing in DomainServicesContext");
  },

  ...overrides,
});

export const DomainServicesContext = React.createContext<Context | null>(null);

export const useDomainServices = (): Context => {
  const domainServices = React.useContext(DomainServicesContext);
  if (!domainServices) {
    throw new Error("DomainServices is null", {
      cause: {
        reason: "DomainServices is accessed outside a DomainServicesProvider",
      },
    });
  }
  return domainServices;
};
